import React from 'react';

// Steps in the wizard process
export const steps = [
  "venue-profile",
  "equipment-inventory",
  "maintenance-history",
  "upcoming-events",
  "system-interactions",
  "past-event-issues",
  "results"
];

// Labels for the wizard steps
export const wizardLabels = [
  "Venue",
  "Equipment",
  "History",
  "Events",
  "Systems",
  "Issues",
  "Results"
];

// Generate form fields based on the current step
export const getFormFields = (step) => {
  switch(step) {
    case 'venue-profile':
      return [
        { id: 'venueName', label: 'Venue Name', type: 'text', required: true },
        { id: 'venueType', label: 'Venue Type', type: 'select', required: true, 
          options: [
            { value: 'concert-venue', label: 'Concert Venue' },
            { value: 'theater', label: 'Theater' },
            { value: 'sports-arena', label: 'Sports Arena' },
            { value: 'conference-center', label: 'Conference Center' },
            { value: 'nightclub', label: 'Nightclub' },
            { value: 'outdoor-festival-grounds', label: 'Outdoor Festival Grounds' },
            { value: 'other', label: 'Other' },
          ]
        },
        { id: 'capacity', label: 'Venue Capacity', type: 'number', required: true },
        { id: 'yearBuilt', label: 'Year Built', type: 'number' },
        { id: 'address', label: 'Address', type: 'textarea' },
        { id: 'contactEmail', label: 'Contact Email', type: 'email' },
        { id: 'phoneNumber', label: 'Phone Number', type: 'tel' },
        { id: 'primaryUse', label: 'Primary Use', type: 'textarea' },
      ];
      
    case 'equipment-inventory':
      return [
        { id: 'systemCategory', label: 'System Category', type: 'select', required: true,
          options: [
            { value: 'audio', label: 'Audio System' },
            { value: 'lighting', label: 'Lighting System' },
            { value: 'video', label: 'Video System' },
            { value: 'hvac', label: 'HVAC' },
            { value: 'electrical', label: 'Electrical' },
            { value: 'rigging', label: 'Rigging & Staging' },
            { value: 'safety', label: 'Safety & Security' },
            { value: 'network', label: 'Network & IT' },
            { value: 'plumbing', label: 'Plumbing' },
            { value: 'other', label: 'Other' },
          ]
        },
        { id: 'manufacturer', label: 'Manufacturer', type: 'text', required: true },
        { id: 'model', label: 'Model', type: 'text', required: true },
        { id: 'installDate', label: 'Install Date', type: 'date' },
        { id: 'lastServiceDate', label: 'Last Service Date', type: 'date' },
        { id: 'condition', label: 'Current Condition', type: 'select', required: true,
          options: [
            { value: 'excellent', label: 'Excellent' },
            { value: 'good', label: 'Good' },
            { value: 'fair', label: 'Fair' },
            { value: 'poor', label: 'Poor' },
            { value: 'critical', label: 'Critical (Needs Immediate Attention)' },
          ]
        },
        { id: 'criticalityLevel', label: 'Criticality Level', type: 'select', required: true,
          options: [
            { value: 'mission-critical', label: 'Mission Critical' },
            { value: 'high', label: 'High' },
            { value: 'medium', label: 'Medium' },
            { value: 'low', label: 'Low' },
          ]
        },
        { id: 'notes', label: 'Additional Notes', type: 'textarea' },
      ];
      
    case 'maintenance-history':
      return [
        { id: 'systemCategory', label: 'System Category', type: 'select', required: true,
          options: [
            { value: 'audio', label: 'Audio System' },
            { value: 'lighting', label: 'Lighting System' },
            { value: 'video', label: 'Video System' },
            { value: 'hvac', label: 'HVAC' },
            { value: 'electrical', label: 'Electrical' },
            { value: 'rigging', label: 'Rigging & Staging' },
            { value: 'safety', label: 'Safety & Security' },
            { value: 'network', label: 'Network & IT' },
            { value: 'plumbing', label: 'Plumbing' },
            { value: 'other', label: 'Other' },
          ]
        },
        { id: 'componentName', label: 'Component Name', type: 'text', required: true },
        { id: 'issueType', label: 'Issue Type', type: 'select', required: true,
          options: [
            { value: 'failure', label: 'Failure' },
            { value: 'degradation', label: 'Performance Degradation' },
            { value: 'preventive', label: 'Preventive Maintenance' },
            { value: 'upgrade', label: 'Upgrade' },
            { value: 'other', label: 'Other' },
          ]
        },
        { id: 'dateDiscovered', label: 'Date Discovered', type: 'date', required: true },
        { id: 'dateResolved', label: 'Date Resolved', type: 'date' },
        { id: 'resolution', label: 'Resolution', type: 'textarea', required: true },
        { id: 'cost', label: 'Cost ($)', type: 'number' },
        { id: 'maintenancePerformedBy', label: 'Maintenance Performed By', type: 'text' },
        { id: 'notes', label: 'Additional Notes', type: 'textarea' },
      ];
      
    case 'upcoming-events':
      return [
        { id: 'eventName', label: 'Event Name', type: 'text', required: true },
        { id: 'eventDate', label: 'Event Date', type: 'date', required: true },
        { id: 'eventType', label: 'Event Type', type: 'select', required: true,
          options: [
            { value: 'concert', label: 'Concert' },
            { value: 'theater', label: 'Theater Performance' },
            { value: 'conference', label: 'Conference' },
            { value: 'sport', label: 'Sporting Event' },
            { value: 'exhibition', label: 'Exhibition' },
            { value: 'convention', label: 'Convention' },
            { value: 'private', label: 'Private Event' },
            { value: 'other', label: 'Other' },
          ]
        },
        { id: 'expectedAttendance', label: 'Expected Attendance', type: 'number', required: true },
        { id: 'eventDuration', label: 'Event Duration (hours)', type: 'number', required: true },
        { id: 'setupTime', label: 'Setup Time Required (hours)', type: 'number' },
        { id: 'criticalSystems', label: 'Critical Systems Needed', type: 'multiselect', required: true,
          options: [
            { value: 'audio', label: 'Audio System' },
            { value: 'lighting', label: 'Lighting System' },
            { value: 'video', label: 'Video System' },
            { value: 'hvac', label: 'HVAC' },
            { value: 'electrical', label: 'Electrical' },
            { value: 'rigging', label: 'Rigging & Staging' },
            { value: 'safety', label: 'Safety & Security' },
            { value: 'network', label: 'Network & IT' },
          ]
        },
        { id: 'notes', label: 'Special Requirements', type: 'textarea' },
      ];
      
    case 'system-interactions':
      return [
        { id: 'primarySystem', label: 'Primary System', type: 'select', required: true,
          options: [
            { value: 'audio', label: 'Audio System' },
            { value: 'lighting', label: 'Lighting System' },
            { value: 'video', label: 'Video System' },
            { value: 'hvac', label: 'HVAC' },
            { value: 'electrical', label: 'Electrical' },
            { value: 'rigging', label: 'Rigging & Staging' },
            { value: 'safety', label: 'Safety & Security' },
            { value: 'network', label: 'Network & IT' },
            { value: 'plumbing', label: 'Plumbing' },
          ]
        },
        { id: 'dependentSystems', label: 'Dependent Systems', type: 'multiselect', required: true,
          options: [
            { value: 'audio', label: 'Audio System' },
            { value: 'lighting', label: 'Lighting System' },
            { value: 'video', label: 'Video System' },
            { value: 'hvac', label: 'HVAC' },
            { value: 'electrical', label: 'Electrical' },
            { value: 'rigging', label: 'Rigging & Staging' },
            { value: 'safety', label: 'Safety & Security' },
            { value: 'network', label: 'Network & IT' },
            { value: 'plumbing', label: 'Plumbing' },
          ]
        },
        { id: 'failureImpact', label: 'Failure Impact', type: 'select', required: true,
          options: [
            { value: 'critical', label: 'Critical - Show Cannot Continue' },
            { value: 'major', label: 'Major - Significant Disruption' },
            { value: 'moderate', label: 'Moderate - Some Disruption' },
            { value: 'minor', label: 'Minor - Limited Impact' },
          ]
        },
        { id: 'failureScenario', label: 'Failure Scenario Description', type: 'textarea' },
        { id: 'notes', label: 'Additional Notes', type: 'textarea' },
      ];
      
    case 'past-event-issues':
      return [
        { id: 'eventTypeAffected', label: 'Event Type Affected', type: 'select', required: true,
          options: [
            { value: 'concert', label: 'Concert' },
            { value: 'theater', label: 'Theater Performance' },
            { value: 'conference', label: 'Conference' },
            { value: 'sport', label: 'Sporting Event' },
            { value: 'exhibition', label: 'Exhibition' },
            { value: 'convention', label: 'Convention' },
            { value: 'private', label: 'Private Event' },
            { value: 'other', label: 'Other' },
          ]
        },
        { id: 'systemAffected', label: 'System Affected', type: 'select', required: true,
          options: [
            { value: 'audio', label: 'Audio System' },
            { value: 'lighting', label: 'Lighting System' },
            { value: 'video', label: 'Video System' },
            { value: 'hvac', label: 'HVAC' },
            { value: 'electrical', label: 'Electrical' },
            { value: 'rigging', label: 'Rigging & Staging' },
            { value: 'safety', label: 'Safety & Security' },
            { value: 'network', label: 'Network & IT' },
            { value: 'plumbing', label: 'Plumbing' },
            { value: 'other', label: 'Other' },
          ]
        },
        { id: 'issueDate', label: 'Date of Issue', type: 'date', required: true },
        { id: 'impactSeverity', label: 'Impact Severity', type: 'select', required: true,
          options: [
            { value: 'critical', label: 'Critical - Show Stopped' },
            { value: 'major', label: 'Major - Significant Disruption' },
            { value: 'moderate', label: 'Moderate - Some Disruption' },
            { value: 'minor', label: 'Minor - Limited Impact' },
          ]
        },
        { id: 'problemDescription', label: 'Problem Description', type: 'textarea', required: true },
        { id: 'resolutionApplied', label: 'Resolution Applied', type: 'textarea' },
        { id: 'downtime', label: 'Downtime (minutes)', type: 'number' },
      ];
      
    default:
      return [];
  }
};

// Generate mock results for demonstration
export const mockResults = () => {
  return {
    summary: {
      equipmentCount: 12,
      criticalItems: 4,
      upcomingEvents: 5,
      predictedIssues: 3
    },
    maintenancePlan: [
      {
        id: 1,
        systemName: 'Main PA System - Left Array',
        maintenanceType: 'Preventive',
        priority: 'Critical',
        dueDate: '2025-05-15',
        estimatedDuration: '3 hours',
        technician: 'Audio Team',
        notes: 'Complete inspection of array rigging and components. Replace any worn hardware.'
      },
      {
        id: 2,
        systemName: 'Dimmer Rack #2',
        maintenanceType: 'Corrective',
        priority: 'High',
        dueDate: '2025-05-10',
        estimatedDuration: '2 hours',
        technician: 'Lighting Team',
        notes: 'Replace faulty modules in positions 5 and 8. Test all circuits after replacement.'
      },
      {
        id: 3,
        systemName: 'Video Projector - Main Stage',
        maintenanceType: 'Preventive',
        priority: 'Medium',
        dueDate: '2025-05-20',
        estimatedDuration: '1 hour',
        technician: 'Video Team',
        notes: 'Clean air filters and check lamp hours. Replace lamp if over 80% of rated life.'
      },
      {
        id: 4,
        systemName: 'Air Handler Unit #3',
        maintenanceType: 'Inspection',
        priority: 'Medium',
        dueDate: '2025-05-12',
        estimatedDuration: '4 hours',
        technician: 'HVAC Contractor',
        notes: 'Seasonal inspection and filter replacement. Check refrigerant levels.'
      },
      {
        id: 5,
        systemName: 'Emergency Lighting System',
        maintenanceType: 'Testing',
        priority: 'High',
        dueDate: '2025-05-05',
        estimatedDuration: '2 hours',
        technician: 'Facilities Team',
        notes: 'Monthly test of all emergency lighting circuits and battery backup systems.'
      }
    ],
    predictedIssues: [
      {
        id: 1,
        systemName: 'Moving Head Lights - Downstage',
        likelihood: 'High',
        predictedFailureDate: '2025-06-15',
        impactLevel: 'Medium',
        recommendation: 'Schedule replacement of cooling fans and clean optics before the June 20th concert.'
      },
      {
        id: 2,
        systemName: 'Wireless Microphone System',
        likelihood: 'Medium',
        predictedFailureDate: '2025-07-05',
        impactLevel: 'High',
        recommendation: 'Battery contacts showing signs of corrosion. Clean and replace affected components before the theater season.'
      },
      {
        id: 3,
        systemName: 'Main Breaker Panel',
        likelihood: 'Low',
        predictedFailureDate: '2025-08-10',
        impactLevel: 'Critical',
        recommendation: 'Thermal imaging shows hotspot on phase B. Schedule inspection with electrician within next 4 weeks.'
      }
    ],
    eventRecommendations: [
      {
        id: 1,
        eventName: 'Summer Concert Series',
        date: '2025-06-01',
        recommendations: [
          'Complete PA system maintenance 2 weeks prior',
          'Test all outdoor lighting fixtures for water ingress',
          'Verify backup power systems',
          'Schedule extra HVAC maintenance to handle crowd heat load'
        ]
      },
      {
        id: 2,
        eventName: 'Symphony Orchestra',
        date: '2025-05-25',
        recommendations: [
          'Verify acoustic treatments are in optimal condition',
          'Ensure HVAC system is set to "quiet mode" operation',
          'Inspect all dimmer packs for noise issues',
          'Test house lighting for smooth fades'
        ]
      }
    ]
  };
};